import Head from 'next/head';

import { getUserListsProductsV3 } from '@/api/lists';
import ListCatalogue from '@/components/ListCatalogue/ListCatalogue';
import { getLayoutProps } from '@/utils/layout/layout';
import { enhanceProductWithSlug } from '@/utils/products/productSlugs.js';
import { isEmpty } from 'lodash';

import PageLayout from '@/components/PageLayout';
import ScrollToTop from '@/components/ScrollToTop/ScrollToTop';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import React, { useEffect, useState, useContext } from 'react';
import { useRouter } from 'next/router';
import { getOrFetchOrderLimitations } from '@/utils/orderLimitations';
import { ListAdditionSheetStore } from '@/store/sideSheet';
import LoaderStore from '@/store/loader';

/* Controllers */
import { getProductsWithCategory } from '@/utils/category';
import User from '@/store/user';
import { getOrFetchUserLists } from '@/utils/getLists';
import { filterObjectsByUniqueSKU } from '@/utils/lists';
import Cookies from 'universal-cookie';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import useProductTags from '@/hooks/useProductTags';
import useBundleOffersSummary from '@/hooks/useBundleOffersSummary';
import { useInfiniteQuery } from '@tanstack/react-query';
import DefaultProductImage from '../../../../../public/assets/wave-grocery/Empty-States-Default/DefaultProductImage.png';
import { usePersonalizedProductsIds } from '@/contexts/PersonalizedProductsIdsContext.jsx';
import { useTranslations } from 'next-intl';
import useDeliveryServices from '@/hooks/useDeliveryServices';

export default function ListsList({ responsiveLogo, image, genericErrorImage, emptyStates }) {
  const router = useRouter();
  const isMobile = useWindowUnderThreshold(850);
  const { productTags } = useProductTags();
  const { bundleOffersSummary } = useBundleOffersSummary();
  const a = useTranslations('common.asides');

  const [products, setProducts] = useState([]);
  const [limitations, setLimitations] = useState([]);
  const [user, setUser] = useState();
  const [numberOfProducts, setNumOfProducts] = useState(0);
  const [selectedList, setSelectedLists] = useState({});
  const [sideSheet, setSideSheet] = useState(false);

  const cookies = new Cookies();
  const authorization = cookies.get('token');
  const { getCollectionTypes } = useContext(DeliveryServicesContext);
  const defaultProdImg = emptyStates?.default_product_image?.url || DefaultProductImage.src;
  const { personalizedProductsIds } = usePersonalizedProductsIds();
  const { shop } = useDeliveryServices();

  useEffect(() => {
    const ListAdditionSheetStore$ = ListAdditionSheetStore.subscribe(setSideSheet);

    return () => {
      ListAdditionSheetStore$.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const user$ = User.subscribe(_user => {
      if (isEmpty(_user)) {
        router.replace('/');
        return;
      }
      setUser(_user);
    });

    async function getLimitations() {
      const limitation = await getOrFetchOrderLimitations();
      setLimitations(limitation);
    }
    getLimitations();
    return () => user$.unsubscribe();
    //eslint-disable-next-line
  }, [router.isReady, router?.query?.id, sideSheet]);

  const handleLoadProducts = async (user, page = 0) => {
    const newProducts = await getUserListsProductsV3(
      user.token,
      router?.query?.id,
      { pageNumber: page, sort: 'createdAt|desc', pageSize: 100 },
      router.locale
    );

    return newProducts;
  };

  const {
    isLoading,
    data,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage: handleLoadMore,
  } = useInfiniteQuery({
    queryKey: ['lists', 'list', router.query.id, shop],
    queryFn: ({ pageParam }) => {
      return handleLoadProducts(user, pageParam);
    },
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.data.pageNumber + 1 >= lastPage.data.totalPages) return undefined;

      return lastPage.data.pageNumber + 1;
    },
    enabled: !!user && !!router.query?.id,
    initialPageParam: 0,
    cacheTime: 0,
    staleTime: 0,
  });

  const fetchAllProducts = async () => {
    let res = { hasNextPage };
    // Doing this to avoid closure with the hasNextPage property
    while (res.hasNextPage) {
      res = await handleLoadMore();
      // Leave in a small timeout to not overload server
      await new Promise(r => setTimeout(r, 100));
    }
  };

  useEffect(() => {
    if (data) {
      let lists = [];
      const selectedCollectionTypes = getCollectionTypes();
      for (const page of data.pages) {
        let newList = filterObjectsByUniqueSKU(page.data.data, selectedCollectionTypes).map(enhanceProductWithSlug);
        setNumOfProducts(page.data?.totalCount || newList.length);
        lists = [...lists, ...newList];
      }

      setProducts({ data: [...lists] });
    }
  }, [data]);

  return (
    <>
      <Head>
        <title>{process.env.displayName}</title>
        <meta name="description" content={`App for ${process.env.displayName} by Wave Grocery`} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href={responsiveLogo} />
      </Head>
      {isMobile ? (
        <>
          <ScrollToTop />
        </>
      ) : null}
      <ListCatalogue
        isLoading={isLoading || !user || !router.query?.id}
        handleLoadMore={handleLoadMore}
        products={getProductsWithCategory(
          products.data,
          undefined,
          productTags,
          bundleOffersSummary,
          router.locale,
          {
            groupByRootCategory: true,
          },
          personalizedProductsIds,
          a
        )}
        limitations={limitations}
        user={user}
        numberOfProducts={numberOfProducts}
        selectedList={selectedList}
        bundleOffersSummary={bundleOffersSummary}
        image={image}
        isFetchingNextPage={isFetchingNextPage}
        genericErrorImage={genericErrorImage}
        defaultProductImage={defaultProdImg}
        fetchAllProducts={fetchAllProducts}
        shop={shop}
      />
    </>
  );
}

ListsList.getLayout = function getLayout(page) {
  return (
    <PageLayout isUserLayout userSelectedMenu={'lists'}>
      {page}
    </PageLayout>
  );
};

export async function getStaticProps(context) {
  const layoutProps = await getLayoutProps({ headerProps: { selectedIndex: 2 } }, context.locale);
  const image = layoutProps?.emptyStates?.favourite_products_empty_states?.url
    ? layoutProps?.emptyStates?.favourite_products_empty_states?.url
    : null;
  const genericErrorImage = layoutProps?.emptyStates?.default_warning?.url ?? null;
  const messages = {
    common: (await import(`../../../../../prebuild-data/i18n/${context.locale}/common.json`)).default,
    user_lists: (await import(`../../../../../prebuild-data/i18n/${context.locale}/user_lists.json`)).default,
  };

  return {
    props: {
      ...layoutProps,
      messages,
      image,
      genericErrorImage,
    },
  };
}
